import React, { useEffect, useState } from "react";
import {
  Tag,
  Badge,
  Empty,
  Table,
  Button,
  Modal,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  LoadingOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { translate } from "../../locale";
import moment from "moment";
import UserServices from "../../services/UserServices";
import { useNavigate, useParams } from "react-router-dom";
import { TableHelper } from "../../helpers/TableHelper";

const PatientMedicineTable = ({ reload }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [modalDetails, setModalDetails] = useState(false);
  const status = {
    active: translate("Activo"),
    inactive: translate("Inactivo"),
    deleted: translate("Borrado"),
  };
  const { username } = useParams();
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
    searchText: "",
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (reload) getPatientMedicines();
  }, [reload]);

  useEffect(() => {
    getPatientMedicines();
  }, [JSON.stringify(tableParams)]);

  const paginationData = () => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null && data.page_size !== 0) {
      data = { ...data, order_by: sorter };
    }
    if (tableParams.searchText !== "") {
      data = { ...data, name: tableParams.searchText };
    }
    return data;
  };

  const getPatientMedicines = async () => {
    setLoading(true);

    try {
      const pagination = paginationData();
      const res = await UserServices.getAssignMedicine(
        username,
        null,
        pagination
      );

      if (res.status >= 200 && res.status < 300) {
        setData(
          res.data.medicines.map((i, k) => {
            let description = `<strong>${translate("Descripción")}:</strong> `;
            if (i.description !== undefined && i.description !== null) {
              description += i.description;
            }
            description += `<br /><strong>${translate("Monodroga")}:</strong> `;
            if (i.monodrug !== undefined && i.monodrug !== null) {
              description += i.monodrug;
            }
            description += `<br /><strong>${translate(
              "Presentación"
            )}:</strong> `;
            if (i.presentation !== undefined && i.presentation !== null) {
              description += i.presentation;
            }
            description += `<br /><strong>${translate("Poder")}:</strong> `;
            if (i.power !== undefined && i.power !== null) {
              description += i.power;
            }
            return {
              key: k,
              product: `${
                i.commercial_brand === "" || i.commercial_brand === null
                  ? ""
                  : i.commercial_brand + " "
              }(${i.name})`,
              description: description,
              date_from: i.date_from,
              date_to: i.date_to,
              status: i.status,
              indication: i.indication,
              subscription_medicine_patient_id:
                i.subscription_medicine_patient_id,
              vademecum_medicine: i.medicine_id,
            };
          })
        );

        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const deletePatientMedicines = async (vademecum_medicine) => {
    setLoading(true);

    try {
      const res = await UserServices.deleteAssignMedicine(
        username,
        vademecum_medicine
      );
      if (res.status >= 200 && res.status < 300) {
        getPatientMedicines();
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const weekDays = ["D", "L", "M", "X", "J", "V", "S"];

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          {record.status !== "deleted" && (
            <Popconfirm
              title={translate("¿Estás seguro de eliminar el registro?")}
              okText={translate("Si")}
              cancelText={translate("No")}
              onConfirm={() => {
                deletePatientMedicines(record.vademecum_medicine);
              }}
              disabled={record.status === "deleted"}
            >
              <Tooltip title={translate("Eliminar")} color="blur">
                <Button
                  style={
                    record.status === "deleted"
                      ? { color: "lightgray", cursor: "pointer" }
                      : null
                  }
                  shape="circle"
                  icon={<DeleteOutlined />}
                  size="small"
                  className="border-none"
                />
              </Tooltip>
            </Popconfirm>
          )}

          {record.status === "deleted" && (
            <Button
              shape="circle"
              icon={<DeleteOutlined />}
              size="small"
              className="border-none"
              disabled={true}
            />
          )}

          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            size="small"
            className="border-0"
            onClick={() => handleShowDetails(record)}
          />
        </div>
      ),
      width: 100,
    },
    {
      title: translate("Producto"),
      dataIndex: "product",
      key: "product",
      render: (_, record) => (
        <div className={record.status !== "active" && "text-red-600"}>
          {record?.product}
        </div>
      ),
      sorter: (a, b) => {
        if (a.product < b.product) return -1;
        if (a.product > b.product) return 1;
        return 0;
      },
    },
    {
      title: translate("Descripción"),
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <div dangerouslySetInnerHTML={{ __html: record.description }}></div>
      ),
      sorter: (a, b) => {
        if (a.description < b.description) return -1;
        if (a.description > b.description) return 1;
        return 0;
      },
    },
    {
      title: translate("Fecha inicio"),
      dataIndex: "date_from",
      key: "date_from",
      render: (_, record) => moment(record.date_from).format("DD/MM/YYYY"),
      sorter: (a, b) => {
        if (a.date_from < b.date_from) return -1;
        if (a.date_from > b.date_from) return 1;
        return 0;
      },
    },
    {
      title: translate("Fecha fin"),
      dataIndex: "date_to",
      key: "date_to",
      render: (_, record) =>
        record.date_to
          ? moment(record.date_to).format("DD/MM/YYYY")
          : translate("Indefinido"),
      sorter: (a, b) => {
        if (a.date_to < b.date_to) return -1;
        if (a.date_to > b.date_to) return 1;
        return 0;
      },
    },
    {
      title: translate("Estado"),
      dataIndex: "status",
      key: "status",
      render: (_, record) => status[record.status],
      sorter: (a, b) => {
        if (status[a.status] < status[b.status]) return -1;
        if (status[a.status] > status[b.status]) return 1;
        return 0;
      },
    },
    {
      title: translate("Indicación"),
      dataIndex: "indication",
      key: "indication",
      width: 260,
      render: (_, record) => {
        return (
          <>
            {[...Array(7)].map((i, k) => {
              const enabled =
                record.indication.days.indexOf(k.toString()) !== -1;
              return (
                <Tag key={i} color={enabled ? "#3BBE50" : "#E8E8E8"}>
                  {weekDays[k]}
                </Tag>
              );
            })}
            <div className="mt-2">
              {record.indication.time.map((tm, k) => (
                <Badge
                  key={tm}
                  className="block"
                  color="geekblue"
                  text={`${tm.substr(0, 5)} hs. (${
                    record.indication.dosage[k]
                  } dosis)`}
                />
              ))}
            </div>
          </>
        );
      },
    },
  ];

  const handleShowDetails = (data) => {
    navigate(
      `/admin/buscar-paciente/${username}/medicinas-paciente/tomas/${data.subscription_medicine_patient_id}`
    );
  };

  const handleHideDetails = () => {
    setModalDetails(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  return (
    <>
      <Table
        className="w-full"
        dataSource={data}
        columns={columns}
        loading={
          loading
            ? { indicator: antIcon, className: "text-custom_blue" }
            : false
        }
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />
      <Modal
        open={modalDetails}
        title={translate("Medicamento")}
        onCancel={handleHideDetails}
        footer={[
          <Button key="ok" type="primary" onClick={handleHideDetails}>
            {translate("Aceptar")}
          </Button>,
        ]}
      >
        {translate("Servicio aun no habilitado")}
      </Modal>
    </>
  );
};

export default PatientMedicineTable;
